import { AnimationClip, AnimationMixer, BufferGeometry, Group, LoopRepeat, Skeleton, SkinnedMesh, TypedArray } from "three";
import { StaticGeometryGenerator } from "three-mesh-bvh";
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader.js";
import { GLTF, GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { KTX2Loader } from "three/examples/jsm/loaders/KTX2Loader.js";
import {CustomGLTFLoader} from './CustomGltfLoader';
import { getAnimationClipByName } from "./worker-utils";

export enum IWorkerDataType{
    initialisation = 'initialisation',
    bufferUpdate = 'bufferUpdate',
    error = 'error',
    updateByTimeStep = 'updateByTimeStep',
    updateTime = 'updateTime',
    updateAnimationData = 'updateAnimationData',
    generateStaticGeometry = 'generateStaticGeometry'
}

export interface IBaseWorkerData { url: string }

interface IBaseInitialisationReturnData extends IBaseWorkerData {type: IWorkerDataType.initialisation}
export interface IInitialisationReturnDataWithNextStep extends IBaseInitialisationReturnData {forceNextStep: IWorkerDataType, id: string}
export type IWorkerInitialisationReturnData = IBaseInitialisationReturnData | IInitialisationReturnDataWithNextStep;

export interface IWorkerUpdateAnimationReturnData extends IBaseWorkerData {type: IWorkerDataType.updateAnimationData}
export interface IWorkerBufferReturnData extends IBaseWorkerData {type: IWorkerDataType.bufferUpdate, attr: TypedArray, index?: TypedArray, id: string, url: string}

interface IBaseErrorData extends IBaseWorkerData {type: IWorkerDataType.error}
export interface IWorkerMissingStateOrArrayBufferErrorData extends IBaseErrorData {error: 'missing-state' | 'array-buffer-parse-error'}
export interface IWorkerMissingGeometryErrorData extends IBaseErrorData {error: 'missing-generate-geometry-data', id: string}
type IWorkerErrorReturnData = IWorkerMissingStateOrArrayBufferErrorData | IWorkerMissingGeometryErrorData;

export type IWorkerReturnData = IWorkerInitialisationReturnData | IWorkerBufferReturnData | IWorkerErrorReturnData | IWorkerUpdateAnimationReturnData;

export interface IModelInstanceWorkerData extends IBaseWorkerData {id: string }

interface IBaseInitalisationData extends IBaseWorkerData {type: IWorkerDataType.initialisation, arrayBuffer: ArrayBuffer, path: string}
export interface IWorkerInitialisationWithNextStep extends IBaseInitalisationData {forceNextStep: IWorkerDataType, id: string}
export type IWorkerInitialisationData = IBaseInitalisationData | IWorkerInitialisationWithNextStep;

export interface IWorkerGenerateGeometryData extends IModelInstanceWorkerData {type: IWorkerDataType.generateStaticGeometry}
export interface IWorkerAnimationData extends IModelInstanceWorkerData {type: IWorkerDataType.updateAnimationData, clampWhenFinished: boolean, repetitions?: number, loop: boolean, name: string}
export interface IWorkerAnimationUpdateTimeStepData extends IModelInstanceWorkerData {type: IWorkerDataType.updateByTimeStep, url: string, timeStep: number, name: string}
export interface IWorkerAnimationUpdateTimeData extends IModelInstanceWorkerData {type: IWorkerDataType.updateTime, time: number, name: string}
export type IWorkerData = IWorkerInitialisationData | IWorkerGenerateGeometryData | IWorkerAnimationUpdateTimeStepData | IWorkerAnimationUpdateTimeData | IWorkerAnimationData;


{
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const loader = new (CustomGLTFLoader as any)() as GLTFLoader;
    const dracoLoader = new DRACOLoader();
    dracoLoader.setDecoderPath('https://d1mfzu0xo6h6ih.cloudfront.net/designer/draco/1.5.6/');
    dracoLoader.setCrossOrigin('anonymous');
    dracoLoader.preload();
    loader.setDRACOLoader(dracoLoader);
    loader.setCrossOrigin('anonymous');

    const ktx2Loader = new KTX2Loader();
    ktx2Loader.setTranscoderPath('https://d1mfzu0xo6h6ih.cloudfront.net/designer/ktx2/7ce26e9/');
    // const renderer = new WebGLRenderer();
    // ktx2Loader.detectSupport( renderer );
    loader.setKTX2Loader(ktx2Loader);

    const generalModelDataDict: {[url: string] : {
        generator: StaticGeometryGenerator;
        animations: AnimationClip[];
        bufferGeometry: BufferGeometry;
        skeletons: Skeleton[];
        hasParseError: boolean;
        scene: Group;
    }} = {};

    const modelInstanceDataDict: {[id: string]: {
        delta: number;
        animationName: string;
        animationTime: number | null;
        missedAnimationTimeDelta: number;
        repetitions: number | undefined;
        clampWhenFinished: boolean;
        loop: boolean;
        previousTime: number;
        mixer: AnimationMixer | null;
        hasIitialTimeStep: boolean;
    }} = {};

    const parseGLTFArrayBuffer = (arrayBuffer: ArrayBuffer, path: string): Promise<GLTF> => {
        return new Promise<GLTF>((resolve,  reject) => {
            loader.parse(arrayBuffer, path, (gltf) => {
                resolve(gltf)
            }, error => reject(error))
        })
    }

    const updateSkeletons = (skeletons: Skeleton[]) => {
        for (let i = 0; i < skeletons.length; i++) {
            const skeleton = skeletons[i];
            for (let j = 0; j < skeleton.bones.length; j++) {
                skeleton.bones[j].updateMatrixWorld(true);
            }
            skeleton.update();     
        }
    }

    const initialiseModelInstanceData = (id: string, url: string) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if (!modelInstanceDataDict[id]) modelInstanceDataDict[id] = {} as any;

        if (!modelInstanceDataDict[id].mixer) {
            modelInstanceDataDict[id].mixer = new AnimationMixer(generalModelDataDict[url].scene);
        }
        return {mixer: modelInstanceDataDict[id].mixer, modelData: modelInstanceDataDict[id]};
    }

    onmessage = async (e: MessageEvent<IWorkerData>) => {

        switch (e.data.type) {
            case IWorkerDataType.initialisation: {
                if (!e.data.arrayBuffer) {
                    console.log('buffer missing: ', e);
                    return;
                }
                
                let scene: Group;
                const { arrayBuffer, path, url } = e.data;

                if (!generalModelDataDict[url]) generalModelDataDict[url] = {
                    skeletons: []
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                } as any;
    
                try {
                    const parseResult = await parseGLTFArrayBuffer(arrayBuffer, path);

                    parseResult.scene.updateMatrixWorld(true);
                    scene = parseResult.scene;

                    if (parseResult.animations.length) {
                        generalModelDataDict[url].animations = parseResult.animations;
                    }  

                } catch (error) {
                    console.error('caught worker parse error:', error)
                    generalModelDataDict[url].hasParseError = true;
                    const returnData: IWorkerErrorReturnData = {type: IWorkerDataType.error, error: 'array-buffer-parse-error', url};
                    postMessage(returnData);
                    return;
                }

                scene.traverse(node => {
                    if ((node as SkinnedMesh).isSkinnedMesh) {
                        if ((node as SkinnedMesh).skeleton) {
                            generalModelDataDict[url].skeletons.push((node as SkinnedMesh).skeleton)
                        }
                    }
                })
                
                generalModelDataDict[url].scene = scene;
                generalModelDataDict[url].generator = new StaticGeometryGenerator(scene);
                generalModelDataDict[url].generator.attributes = ['position'];
                if (!generalModelDataDict[url].bufferGeometry) generalModelDataDict[url].bufferGeometry = new BufferGeometry();
                
                const returnData: IWorkerInitialisationReturnData = {type: IWorkerDataType.initialisation, url};
                const data = e.data as IWorkerInitialisationWithNextStep;
                if (data.forceNextStep) {
                    (returnData as IInitialisationReturnDataWithNextStep).forceNextStep = data.forceNextStep;
                    (returnData as IInitialisationReturnDataWithNextStep).id = data.id;
                }
                postMessage(returnData);
                break;
            }
            case IWorkerDataType.generateStaticGeometry: {
                const { url, id } = e.data;
                if (!generalModelDataDict[url]) {
                    console.warn('missing general model data - triggering re-initialisation: ', url);
                    const errorReturnData: IWorkerErrorReturnData = {type: IWorkerDataType.error, url, error: 'missing-generate-geometry-data', id};
                    postMessage(errorReturnData);
                    return;
                }
                const bufferGeometry = generalModelDataDict[url].generator.generate()
                const returnData: IWorkerBufferReturnData = {type: IWorkerDataType.bufferUpdate, attr: bufferGeometry.attributes.position.array, index: bufferGeometry.getIndex()?.array, id, url};
                postMessage(returnData);
                break;
            }
            case IWorkerDataType.updateAnimationData: {
                const { id, clampWhenFinished, repetitions, loop, name, url } = e.data;
                
                const {mixer, modelData} = initialiseModelInstanceData(id, url);
                mixer?.stopAllAction()
             
                modelInstanceDataDict[id] = {
                    ...modelData, 
                    clampWhenFinished, 
                    repetitions, 
                    loop,
                    animationName: name,
                    delta: 0,
                    missedAnimationTimeDelta: 0,
                    previousTime: 0
                } 

                const returnData: IWorkerUpdateAnimationReturnData = {type: IWorkerDataType.updateAnimationData, url};
                postMessage(returnData);
                break;
            }
            case IWorkerDataType.updateByTimeStep: {
                const { timeStep, name, url, id } = e.data;

                if (!modelInstanceDataDict[id].hasIitialTimeStep) {
                    modelInstanceDataDict[id].hasIitialTimeStep = true;
                }

                const {mixer} = initialiseModelInstanceData(id, url);
                mixer?.stopAllAction();

                const { hasParseError, animations, generator, skeletons, bufferGeometry } = generalModelDataDict[url] || {};

                if (hasParseError || (!animations && !!generator)) {
                    return
                }

                if (!animations || !generator || !mixer) {
                    modelInstanceDataDict[id].missedAnimationTimeDelta += timeStep;
                    const returnData: IWorkerErrorReturnData = {type: IWorkerDataType.error, error: 'missing-state', url};
                    postMessage(returnData);
                    return;
                } 

                const { loop, repetitions, clampWhenFinished } = modelInstanceDataDict[id];
                const _repetitions = loop ? Infinity : repetitions ?? Infinity;
                const animationClip = getAnimationClipByName(animations, name);
                if (!animationClip) {
                    console.error('Unable to set mixer: no animation action found')
                    return;
                };
                const animationAction = mixer.clipAction(animationClip);
                animationAction.reset().clampWhenFinished = !!clampWhenFinished;
                animationAction.play().setLoop(LoopRepeat, _repetitions);
                // ensure the mixer is set to the model instance specific start time before time increment. 
                mixer.setTime(modelInstanceDataDict[id]?.previousTime ?? 0)
                mixer.update(timeStep + (modelInstanceDataDict[id].missedAnimationTimeDelta ?? 0));          
                modelInstanceDataDict[id].missedAnimationTimeDelta = 0;
                modelInstanceDataDict[id].previousTime = mixer.time;

                updateSkeletons(skeletons);

                generator.generate(bufferGeometry)

                const returnData: IWorkerBufferReturnData = {type: IWorkerDataType.bufferUpdate, attr: bufferGeometry.attributes.position.array, index: bufferGeometry.getIndex()?.array, id, url};
                postMessage(returnData);
                break;
            }
            case IWorkerDataType.updateTime: {
                const { time, name, url, id } = e.data

                const {mixer} = initialiseModelInstanceData(id, url);
                mixer?.stopAllAction();

                modelInstanceDataDict[id].animationName = name;
                modelInstanceDataDict[id].animationTime = time;
                modelInstanceDataDict[id].previousTime = time;

                const { animationName } = modelInstanceDataDict[id];

                const {hasParseError, animations, skeletons, generator, bufferGeometry} = generalModelDataDict[url] || {};

                if (hasParseError || (!animations && !!generator)) return;

                if (!animations || !generator || !mixer) {
                    const returnData: IWorkerErrorReturnData = {type: IWorkerDataType.error, error: 'missing-state', url};
                    postMessage(returnData);
                    return;
                } 

                const idleAction = getAnimationClipByName(animations, animationName); 
                if (!idleAction) return;
                mixer.clipAction(idleAction)?.reset()?.play();          
                mixer.setTime(time);

                updateSkeletons(skeletons);

                generator.generate(bufferGeometry)

                const returnData: IWorkerBufferReturnData = {
                    type: IWorkerDataType.bufferUpdate, 
                    attr: bufferGeometry.attributes.position.array,
                    index: bufferGeometry.index?.array,
                    id, 
                    url
                };
                postMessage(returnData)
                break;
            }
            default:
                break;
        }
    }
}